import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const monthLabel = (m, pt) => new Date(m + '-01T00:00:00').toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { month: 'short', year: 'numeric' });

const FinanceAirbnbReceivables = ({ language, token, onOpenAirbnb }) => {
  const pt = language === 'pt';
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/airbnb-receivables`, { headers }); setData(r.data); } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (item) => {
    const method = window.prompt(pt ? `Marcar ${eur(item.total)} de ${item.name} (${monthLabel(item.month, true)}) como pago. Método:` : `Mark ${eur(item.total)} from ${item.name} (${monthLabel(item.month, false)}) as paid. Method:`, pt ? 'Transferência' : 'Bank transfer');
    if (method === null) return;
    setBusy(true);
    try { await axios.put(`${API}/admin/airbnb-invoices/${item.month}/${encodeURIComponent(item.host_key)}/payment`, { status: 'paid', method, amount: item.total }, { headers }); await load(); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const pending = data.items.filter(i => i.status !== 'paid');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-rose-100 p-4" data-testid="finance-airbnb-receivables">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center">🏡</div>
          <div>
            <p className="text-sm text-gray-500 flex items-center gap-1"><Wallet size={14} /> {pt ? 'Airbnb — por receber' : 'Airbnb — outstanding'}</p>
            <p className={`text-2xl font-bold ${data.total_outstanding > 0 ? 'text-amber-600' : 'text-green-600'}`} data-testid="airbnb-outstanding-total">{eur(data.total_outstanding)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {data.total_overdue > 0 && (
            <span className="inline-flex items-center gap-1 text-red-600 font-medium" data-testid="airbnb-overdue-total"><AlertTriangle size={14} /> {pt ? 'Em atraso' : 'Overdue'}: {eur(data.total_overdue)}</span>
          )}
          <span className="inline-flex items-center gap-1 text-green-700" data-testid="airbnb-paid-total"><CheckCircle2 size={14} /> {pt ? 'Recebido' : 'Received'}: {eur(data.total_paid)}</span>
          <span className="text-gray-500">{data.pending_count} {pt ? 'fatura(s) pendente(s)' : 'pending invoice(s)'}</span>
          {pending.length > 0 && (
            <button onClick={() => setOpen(o => !o)} className="p-1.5 rounded-lg border hover:bg-gray-50" data-testid="airbnb-receivables-toggle">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
          )}
        </div>
      </div>
      {open && pending.length > 0 && (
        <div className="mt-4 border-t pt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="airbnb-receivables-table">
            <thead className="text-left text-gray-500 text-xs">
              <tr><th className="py-1.5 pr-3">{pt ? 'Mês' : 'Month'}</th><th className="py-1.5 pr-3">{pt ? 'Anfitrião' : 'Host'}</th><th className="py-1.5 pr-3">{pt ? 'Limpezas' : 'Cleanings'}</th><th className="py-1.5 pr-3">{pt ? 'Valor' : 'Amount'}</th><th className="py-1.5"></th></tr>
            </thead>
            <tbody>
              {pending.map(i => (
                <tr key={`${i.month}-${i.host_key}`} className="border-t" data-testid={`receivable-${i.month}-${i.host_key}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{monthLabel(i.month, pt)} {i.overdue && <span className="ml-1 text-[10px] uppercase bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">{pt ? 'atraso' : 'overdue'}</span>}</td>
                  <td className="py-2 pr-3"><div className="font-medium text-gray-900">{i.name}</div><div className="text-xs text-gray-400">{i.phone}</div></td>
                  <td className="py-2 pr-3">{i.count}</td>
                  <td className="py-2 pr-3 font-semibold text-amber-700">{eur(i.total)}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => markPaid(i)} disabled={busy} data-testid="receivable-mark-paid" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-[#1a5c3a] text-white hover:bg-[#164d31] disabled:opacity-60"><CheckCircle2 size={12} /> {pt ? 'Recebido' : 'Received'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {onOpenAirbnb && <button onClick={onOpenAirbnb} className="mt-3 text-xs text-rose-600 hover:underline" data-testid="airbnb-receivables-open">{pt ? 'Ver faturas Airbnb →' : 'View Airbnb invoices →'}</button>}
        </div>
      )}
    </div>
  );
};

export default FinanceAirbnbReceivables;
